import React, { useState, useRef, useEffect, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { engine } from "@/lib/drumAudio";
import { Play, Pause, Hand } from "lucide-react";

const SIGS = {
  "4/4": 4,
  "3/4": 3,
  "6/8": 6,
  "7/8": 7,
};

export default function Metronome({ open, onOpenChange }) {
  const [bpm, setBpm] = useState(120);
  const [sig, setSig] = useState("4/4");
  const [running, setRunning] = useState(false);
  const [beat, setBeat] = useState(-1);

  const schedulerRef = useRef(null);
  const nextTimeRef = useRef(0);
  const beatCountRef = useRef(0);
  const beatsPerBar = SIGS[sig];

  const stop = useCallback(() => {
    if (schedulerRef.current) clearInterval(schedulerRef.current);
    schedulerRef.current = null;
    setRunning(false);
    setBeat(-1);
  }, []);

  const start = useCallback(() => {
    engine.ensure();
    nextTimeRef.current = engine.now + 0.1;
    beatCountRef.current = 0;
    const secondsPerBeat = 60 / bpm;
    schedulerRef.current = setInterval(() => {
      while (nextTimeRef.current < engine.now + 0.15) {
        const idx = beatCountRef.current % beatsPerBar;
        const accent = idx === 0;
        engine.click(nextTimeRef.current, accent);
        const scheduled = nextTimeRef.current;
        const uiDelay = Math.max(0, (scheduled - engine.now) * 1000);
        setTimeout(() => setBeat(idx), uiDelay);
        nextTimeRef.current += secondsPerBeat;
        beatCountRef.current += 1;
      }
    }, 25);
    setRunning(true);
  }, [bpm, beatsPerBar]);

  const toggle = () => (running ? stop() : start());

  // restart on bpm/sig change while running
  useEffect(() => {
    if (running) {
      stop();
      start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bpm, sig]);

  // stop on close
  useEffect(() => {
    if (!open) stop();
  }, [open, stop]);

  useEffect(() => () => stop(), [stop]);

  // tap tempo
  const tapsRef = useRef([]);
  const tap = () => {
    const now = performance.now();
    tapsRef.current = tapsRef.current.filter((t) => now - t < 2500);
    tapsRef.current.push(now);
    if (tapsRef.current.length >= 2) {
      const intervals = [];
      for (let i = 1; i < tapsRef.current.length; i++) {
        intervals.push(tapsRef.current[i] - tapsRef.current[i - 1]);
      }
      const avg = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      const newBpm = Math.round(60000 / avg);
      if (newBpm >= 40 && newBpm <= 240) setBpm(newBpm);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-[#0b0c10] border-white/10 w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="font-heading text-3xl uppercase tracking-tight text-left">
            Metro<span className="cyan-text">nome</span>
          </SheetTitle>
        </SheetHeader>

        <div className="mt-8 flex flex-col items-center">
          <div className="flex gap-3 mb-8">
            {Array.from({ length: beatsPerBar }).map((_, i) => (
              <div
                key={i}
                data-testid={`metronome-beat-${i}`}
                className="w-7 h-7 rounded-full transition-all duration-75"
                style={{
                  background:
                    beat === i ? (i === 0 ? "#FFAB00" : "#00F0FF") : "rgba(255,255,255,0.08)",
                  boxShadow:
                    beat === i
                      ? `0 0 20px ${i === 0 ? "#FFAB00" : "#00F0FF"}`
                      : "none",
                }}
              />
            ))}
          </div>

          <div className="text-center mb-2">
            <div className="font-heading text-7xl font-extrabold tabular-nums cyan-text">{bpm}</div>
            <div className="text-xs tracking-[0.3em] uppercase text-slate-500 font-tab">BPM</div>
          </div>

          <div className="w-full px-2 my-6">
            <Slider
              value={[bpm]}
              min={40}
              max={240}
              step={1}
              onValueChange={(v) => setBpm(v[0])}
              data-testid="metronome-bpm-slider"
            />
          </div>

          <div className="w-full flex items-center gap-3 mb-6">
            <Select value={sig} onValueChange={setSig}>
              <SelectTrigger className="bg-[#12161f] border-white/10" data-testid="metronome-sig-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#12161f] border-white/10">
                {Object.keys(SIGS).map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={tap}
              className="flex-1 border-white/15 bg-white/5 hover:bg-white/10"
              data-testid="metronome-tap-tempo-button"
            >
              <Hand className="w-4 h-4 mr-2" /> Tap Tempo
            </Button>
          </div>

          <Button
            onClick={toggle}
            className={`w-full h-14 font-bold uppercase tracking-wide ${
              running
                ? "bg-[#FF3366] hover:bg-[#ff4d7a] text-white"
                : "bg-[#00f0ff] hover:bg-[#33f4ff] text-black glow-cyan"
            }`}
            data-testid="metronome-play-button"
          >
            {running ? (
              <><Pause className="w-5 h-5 mr-2" /> Stop</>
            ) : (
              <><Play className="w-5 h-5 mr-2" /> Start</>
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
